import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";


const estimateSchema = z.object({
  // Basic Information
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email"),
  phone: z.string().min(10, "Phone must have at least 10 digits"),
  
  // Address fields
  addressLine1: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().regex(/^[A-Z]{2}$/, "State must be 2 letters (e.g., CT)"),
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, "ZIP must be 5 digits (e.g., 06000)"),
  
  // Property Details
  propertyType: z.string().min(1, "Property type is required"),
  propertySize: z.number().min(1, "Property size is required"),
  
  // Residential fields
  bedrooms: z.number().optional(),
  bathrooms: z.number().optional(),
  
  // Service Details
  serviceType: z.string().min(1, "Service type is required"),
  frequency: z.string().min(1, "Frequency is required"),
  urgency: z.string().min(1, "Urgency is required"),
  additionalServices: z.array(z.string()).optional(),
  
  // Contact Preferences
  preferredContact: z.enum(["phone", "text", "email"], {
    required_error: "Please select a contact method"
  }),
  
  // Additional Info (unified field)
  additionalInfo: z.string().max(2000).optional(),
  
  // Referral tracking
  referredBy: z.string().max(100).optional(),
  
  // Honeypot
  website: z.string().max(0, "Bot detected")
}).superRefine((data, ctx) => {
  // Conditional validation based on property type
  const isResidential = ["house", "apartment", "condo"].includes(data.propertyType);

  if (isResidential) {
    if (!data.bedrooms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Number of bedrooms is required",
        path: ["bedrooms"]
      });
    }
    if (!data.bathrooms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Number of bathrooms is required",
        path: ["bathrooms"]
      });
    }
  }
});

type EstimateFormData = z.infer<typeof estimateSchema>;

const EstimateForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const totalSteps = 2;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<EstimateFormData>({
    resolver: zodResolver(estimateSchema),
    defaultValues: {}
  });

  const watchedValues = watch();
  const propertyType = watch("propertyType");

  // Get required fields for current step
  const getRequiredFieldsForStep = (step: number): Array<keyof EstimateFormData> => {
    switch (step) {
      case 1:
        return ["firstName", "lastName", "email", "phone", "addressLine1", "city", "state", "zip", "preferredContact"];
      case 2:
        const step2Fields: Array<keyof EstimateFormData> = ["propertyType", "propertySize", "serviceType", "frequency", "urgency"];
        
        // Add conditional fields based on property type
        if (propertyType && ["house", "apartment", "condo"].includes(propertyType)) {
          step2Fields.push("bedrooms", "bathrooms");
        }
        return step2Fields;
      default:
        return [];
    }
  };

  // Validate current step
  const validateCurrentStep = async (): Promise<boolean> => {
    const fieldsToValidate = getRequiredFieldsForStep(currentStep);
    const result = await trigger(fieldsToValidate);
    return result;
  };

  // Clear conditional fields when property type changes
  const handlePropertyTypeChange = (value: string) => {
    setValue("propertyType", value);
    
    // Clear residential fields if switching away from residential
    const isResidential = ["house", "apartment", "condo"].includes(value);
    if (!isResidential) {
      setValue("bedrooms", undefined);
      setValue("bathrooms", undefined);
      clearErrors(["bedrooms", "bathrooms"]);
    }
  };

  const onSubmit = async (data: EstimateFormData) => {
    if (data.website) {
      toast.error("Submission blocked - spam detected");
      return;
    }

    try {
      const isResidential = ["house", "apartment", "condo"].includes(data.propertyType);

      const supabaseData = {
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        phone: data.phone,
        property_type: data.propertyType,
        bedrooms: isResidential ? (data.bedrooms || null) : null,
        bathrooms: isResidential ? (data.bathrooms || null) : null,
        square_feet: data.propertySize || null,
        building_type: null,
        office_spaces: null,
        employees: null,
        address_line1: data.addressLine1,
        address_line2: null,
        city: data.city,
        state: data.state,
        zip_code: data.zip,
        service_types: data.serviceType ? [data.serviceType] : ['not-specified'],
        service_frequency: data.frequency || 'not-specified',
        preferred_contact: data.preferredContact || 'phone',
        preferred_date: null,
        preferred_time: null,
        additional_details: data.additionalInfo || null,
        referred_by: data.referredBy || null
      };

      const { error } = await supabase.functions.invoke('save-estimate', {
        body: supabaseData
      });

      if (error) {
        console.error('Error saving estimate:', error);
        toast.error("Error submitting request. Please try again or call us directly.");
        return;
      }

      // Send email notification
      const fullName = `${data.firstName} ${data.lastName}`;
      const address = `${data.addressLine1}, ${data.city}, ${data.state} ${data.zip}`;
      await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'estimate-request',
          recipientEmail: 'contact@routinecleaners.com',
          idempotencyKey: `estimate-${Date.now()}`,
          templateData: {
            name: fullName,
            email: data.email,
            phone: data.phone,
            propertyType: data.propertyType,
            squareFeet: data.propertySize,
            bedrooms: isResidential ? data.bedrooms : undefined,
            bathrooms: isResidential ? data.bathrooms : undefined,
            serviceType: data.serviceType,
            frequency: data.frequency,
            preferredContact: data.preferredContact,
            address,
            additionalDetails: data.additionalInfo,
            referredBy: data.referredBy,
          },
        }
      });

      setShowSuccessDialog(true);
      reset();
      setCurrentStep(1);
    } catch (error) {
      console.error('Error submitting estimate:', error);
      toast.error("An unexpected error occurred. Please try again or call us directly.");
    }
  };

  const nextStep = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      toast.error("Please fill in all required fields before continuing");
      return;
    }
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Contact Information and Property Details";
      case 2:
        return "Service Details and Additional Information";
      default:
        return "Estimate Form";
    }
  };

  const getContactMethodLabel = (method: string) => {
    switch (method) {
      case 'phone':
        return 'Phone Call';
      case 'text':
        return 'Text Message';
      case 'email':
        return 'Email';
      default:
        return method;
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            {getStepTitle()}
          </CardTitle>
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of {totalSteps}
            </span>
            <div className="flex space-x-1">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${i + 1 <= currentStep ? 'bg-accent' : 'bg-muted'}`}
                />
              ))}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Step 1: Contact Information & Property Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input 
                      id="firstName" 
                      {...register("firstName")} 
                      placeholder="John" 
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive mt-1">{errors.firstName.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input 
                      id="lastName" 
                      {...register("lastName")} 
                      placeholder="Smith" 
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      type="tel" 
                      {...register("phone")} 
                      placeholder="(475) 123-4567" 
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    {...register("email")} 
                    placeholder="your.email@example.com" 
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                  )}
                </div>

                {/* Honeypot field - hidden from users */}
                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <Label htmlFor="website">Website (leave blank)</Label>
                  <Input 
                    id="website" 
                    {...register("website")} 
                    tabIndex={-1} 
                    autoComplete="off" 
                  />
                </div>

                {/* Separate address fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="addressLine1">Street Address *</Label>
                    <Input 
                      id="addressLine1" 
                      {...register("addressLine1")} 
                      placeholder="123 Main Street" 
                    />
                    {errors.addressLine1 && (
                      <p className="text-sm text-destructive mt-1">{errors.addressLine1.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input 
                      id="city" 
                      {...register("city")} 
                      placeholder="Hartford" 
                    />
                    {errors.city && (
                      <p className="text-sm text-destructive mt-1">{errors.city.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input 
                      id="state" 
                      {...register("state")} 
                      placeholder="CT" 
                      maxLength={2} 
                      className="uppercase"
                      onChange={(e) => setValue("state", e.target.value.toUpperCase())}
                    />
                    {errors.state && (
                      <p className="text-sm text-destructive mt-1">{errors.state.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input 
                      id="zip" 
                      {...register("zip")} 
                      placeholder="06000" 
                      maxLength={10} 
                    />
                    {errors.zip && (
                      <p className="text-sm text-destructive mt-1">{errors.zip.message}</p>
                    )}
                  </div>
                </div>

                {/* Preferred Contact Method - Moved to Step 1 */}
                <div>
                  <Label htmlFor="preferredContact">How would you like us to contact you? *</Label>
                  <Select onValueChange={(value) => setValue("preferredContact", value as "phone" | "text" | "email")}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose contact method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="text">Text Message</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.preferredContact && (
                    <p className="text-sm text-destructive mt-1">{errors.preferredContact.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Service Details & Additional Information */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="propertyType">Property Type *</Label>
                    <Select onValueChange={handlePropertyTypeChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="house">House</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.propertyType && (
                      <p className="text-sm text-destructive mt-1">{errors.propertyType.message}</p>
                    )}
                  </div>

                  {/* Property Size - Manual Input for ALL types */}
                  <div>
                    <Label htmlFor="propertySize">Property Size (Square Feet) *</Label>
                    <Input 
                      id="propertySize" 
                      type="number" 
                      placeholder="Enter square feet (e.g., 1500)"
                      onChange={(e) => setValue("propertySize", parseInt(e.target.value) || 0)}
                      min="1"
                    />
                    {errors.propertySize && (
                      <p className="text-sm text-destructive mt-1">{errors.propertySize.message}</p>
                    )}
                  </div>
                </div>

                {/* Residential specific fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bedrooms">Number of Bedrooms *</Label>
                    <Input 
                      id="bedrooms" 
                      type="number" 
                      placeholder="e.g., 3"
                      min="0"
                      max="20"
                      onChange={(e) => setValue("bedrooms", parseInt(e.target.value) || 0)}
                    />
                    {errors.bedrooms && (
                      <p className="text-sm text-destructive mt-1">{errors.bedrooms.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="bathrooms">Number of Bathrooms *</Label>
                    <Input 
                      id="bathrooms" 
                      type="number" 
                      step="0.5"
                      placeholder="e.g., 2.5"
                      min="0.5"
                      max="10"
                      onChange={(e) => setValue("bathrooms", parseFloat(e.target.value) || 0)}
                    />
                    {errors.bathrooms && (
                      <p className="text-sm text-destructive mt-1">{errors.bathrooms.message}</p>
                    )}
                  </div>
                </div>

                {/* Service Details Section */}
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-lg font-medium mb-4">Service Details</h3>
                  
                  <div>
                    <Label htmlFor="serviceType">Type of Cleaning Service *</Label>
                    <Select onValueChange={(value) => setValue("serviceType", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deep">Deep Cleaning</SelectItem>
                        <SelectItem value="regular">Regular House Cleaning</SelectItem>
                        <SelectItem value="one-time">One Time Cleaning</SelectItem>
                        <SelectItem value="move-in">Move In Cleaning</SelectItem>
                        <SelectItem value="move-out">Move Out Cleaning</SelectItem>
                        <SelectItem value="spring">Spring Cleaning</SelectItem>
                        <SelectItem value="holiday">Holiday Cleaning</SelectItem>
                        <SelectItem value="post-construction">Post-Construction Cleaning</SelectItem>
                        <SelectItem value="post-renovation">Post Renovation Cleaning</SelectItem>
                        <SelectItem value="airbnb">Airbnb Cleaning</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.serviceType && (
                      <p className="text-sm text-destructive mt-1">{errors.serviceType.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="frequency">Cleaning Frequency *</Label>
                      <Select onValueChange={(value) => setValue("frequency", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="one-time">One-time</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.frequency && (
                        <p className="text-sm text-destructive mt-1">{errors.frequency.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="urgency">When do you need this service? *</Label>
                      <Select onValueChange={(value) => setValue("urgency", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select urgency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asap">As soon as possible</SelectItem>
                          <SelectItem value="this-week">This week</SelectItem>
                          <SelectItem value="next-week">Next week</SelectItem>
                          <SelectItem value="flexible">I'm flexible</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.urgency && (
                        <p className="text-sm text-destructive mt-1">{errors.urgency.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Additional Services Section */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <h3 className="text-lg font-medium mb-4">Additional Services</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Select any additional services you'd like to include (these are charged separately):
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        "Inside refrigerator cleaning",
                        "Inside oven cleaning", 
                        "Inside cabinets cleaning",
                        "Window cleaning (interior and exterior)"
                      ].map((service) => (
                        <div key={service} className="flex items-start space-x-2">
                          <Checkbox 
                            id={service}
                            className="mt-0.5"
                            onCheckedChange={(checked) => {
                              const currentServices = watchedValues.additionalServices || [];
                              if (checked) {
                                setValue("additionalServices", [...currentServices, service]);
                              } else {
                                setValue("additionalServices", currentServices.filter(s => s !== service));
                              }
                            }}
                          />
                          <Label htmlFor={service} className="text-sm font-normal leading-normal cursor-pointer">{service}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Questions or Additional Information - Unified Field */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="mt-4">
                      <Label htmlFor="additionalInfo">Questions or Additional Information</Label>
                      <Textarea 
                        id="additionalInfo"
                        {...register("additionalInfo")}
                        placeholder="Tell us about any special requests, questions, or specific needs you may have..."
                        rows={8}
                        className="resize-none"
                      />
                      {errors.additionalInfo && (
                        <p className="text-sm text-destructive mt-1">{errors.additionalInfo.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Referral Tracking */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <div>
                      <Label htmlFor="referredBy">Were you referred by someone?</Label>
                      <Input 
                        id="referredBy"
                        {...register("referredBy")}
                        placeholder="Enter referrer's name (optional)"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        If someone referred you to us, let us know so we can thank them!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-border">
              {currentStep > 1 && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button type="button" onClick={nextStep} className="ml-auto">
                  Next Step
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting} className="ml-auto">
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Success Dialog with Next Steps */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="relative">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <DialogTitle className="text-center text-2xl">Request Submitted Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              Thank you for requesting an estimate! Here's what happens next.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-center text-muted-foreground">
              Thank you for choosing Routine Cleaners! Here's what happens next:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">1</div>
                <div className="flex-1">
                  <p className="font-medium">We'll review your request</p>
                  <p className="text-sm text-muted-foreground">Our team will carefully analyze your needs</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">2</div>
                <div className="flex-1">
                  <p className="font-medium">We'll contact you within 2-4 hours</p>
                  <p className="text-sm text-muted-foreground">
                    We'll contact you via your preferred method to collect additional info
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">3</div>
                <div className="flex-1">
                  <p className="font-medium">Receive your personalized estimate</p>
                  <p className="text-sm text-muted-foreground">Detailed pricing based on your specific needs</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">4</div>
                <div className="flex-1">
                  <p className="font-medium">Schedule your cleaning</p>
                  <p className="text-sm text-muted-foreground">Choose a time that works best for you</p>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">Have questions?</p>
              <Button variant="outline" asChild>
                <a href="tel:+14752517156">
                  <Phone className="mr-2 h-4 w-4" />
                  Call us now: (475) 251-7156
                </a>
              </Button>
            </div>

            {/* Referral Program Mention */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-muted-foreground text-center">
                Know someone who could use our services?{" "}
                <a href="/referral" className="text-accent hover:underline font-medium">
                  Refer them and earn rewards!
                </a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EstimateForm;
